import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { enqueueSnackbar } from "notistack";

import Input from "@/components/input/Input";
import Button from "@/components/input/Button";
import Select from "@/components/input/Select";
import Spinner from "@/components/dataDisplay/Spinner";
import Textarea from "@/components/input/Textarea";

import { Route } from "@/services/routes/route.vm";
import { routes } from "@/constants/routes";
import useLearningRoutes from "@/context/routes/hook";
import { routesService } from "@/services/routes";
import { CoverImage } from "./CoverImage";
import DeleteButton from "./DeleteButton";

interface CreateEditRouteFormProps {
  routeId?: string;
}

const CreateEditRouteForm: React.FC<CreateEditRouteFormProps> = ({
  routeId,
}) => {
  const navigate = useNavigate();
  const { addOrReplaceRoute, getRouteById } = useLearningRoutes();
  const [loading, setLoading] = useState(false);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState<Route["type"]>("FOUNDATION");
  const [coverImage, setCoverImage] = useState<null | string | File>(null);

  const handleCancel: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    navigate(routes.admin.routes.root);
  };

  const handleReset = () => {
    setTitle("");
    setDescription("");
    setType("FOUNDATION");
    setCoverImage(null);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const submitter = (e.nativeEvent as Event & { submitter: HTMLElement })
      .submitter as HTMLButtonElement;

    const form = e.currentTarget;
    const formData = new FormData();

    formData.append("title", title);
    formData.append("type", type);
    formData.append("description", description);
    if (coverImage && typeof coverImage !== "string") {
      formData.append("coverImage", coverImage);
    }

    const service = () =>
      routeId
        ? routesService.updateRoute(routeId, formData)
        : routesService.createRoute(formData);

    service()
      .then((route) => {
        addOrReplaceRoute(route);
        if (submitter.name === "save") {
          navigate(-1);
        } else if (submitter.name === "saveAndAddAnother") {
          if (!routeId) {
            handleReset();
            form.reset();
          }
        }
        const toasterMessage = routeId
          ? "Ruta actualizada correctamente"
          : "Ruta creada correctamente";
        enqueueSnackbar(toasterMessage, {
          variant: "success",
        });
      })
      .catch((error) => {
        enqueueSnackbar("Error al crear o editar la ruta", {
          variant: "error",
        });
        console.error("Error creating or editing route", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (routeId) {
      const route = getRouteById(routeId);
      if (route) {
        setTitle(route.title);
        setDescription(route.description);
        setType(route.type);
        setCoverImage(route.coverImage ?? null);
      }
    }
  }, [getRouteById, routeId]);

  return (
    <form
      style={{ display: "flex", flexDirection: "column", gap: 16 }}
      onSubmit={handleSubmit}
    >
      <CoverImage image={coverImage} setImage={setCoverImage} />
      <Input
        name="title"
        required
        type="text"
        placeholder="Title"
        value={title}
        label="Título"
        helperText="Este título se mostrará a todos los usuarios y servirá para identificar la ruta."
        onChange={(e) => setTitle(e.target.value)}
      />
      <Select
        name="type"
        required
        value={type}
        label="Tipo"
        helperText="Determina el tipo de ruta que se podrá crear y mostrar a los usuarios."
        onChange={(e) => setType(e.target.value as Route["type"])}
      >
        <option value="FOUNDATION">Foundation</option>
        <option value="CUSTOM">Custom</option>
      </Select>
      <Textarea
        name="description"
        required
        placeholder="Description"
        value={description}
        label="Descripción"
        helperText="Esta descripción se mostrará a todos los usuarios."
        onChange={(e) => setDescription(e.target.value)}
      ></Textarea>
      <div
        style={{
          display: "flex",
          gap: 16,
          justifyContent: "space-between",
          ...(loading && { opacity: 0.5 }),
        }}
      >
        {!routeId ? (
          <Button
            type="button"
            variant="danger"
            onClick={handleCancel}
            disabled={loading}
          >
            Cancelar
          </Button>
        ) : (
          <DeleteButton routeId={routeId} loading={loading} />
        )}
        <div style={{ display: "flex", flexDirection: "row", gap: 16 }}>
          <Button
            type="submit"
            name="saveAndAddAnother"
            variant="outline"
            disabled={loading}
          >
            {routeId ? "Guardar y seguir editando" : "Guardar y añadir otro"}
          </Button>
          <Button type="submit" name="save" disabled={loading}>
            {loading && <Spinner style={{ marginRight: 8 }} />}
            Guardar
          </Button>
        </div>
      </div>
    </form>
  );
};

export default CreateEditRouteForm;
